import type { CSSVariablesResolver } from "@mantine/core";

/**
 * Named surface, border, and text tokens (docs/UI_STANDARDS.md, "Lucid-Inspired Surface, Density, and Color
 * Direction"). This module and `palette.ts` are the only places in the repository where product colors are written as
 * hex; everything else consumes the CSS variables these emit, Mantine color names, or semantic roles.
 *
 * The ladder is page → sunken → panel → raised → floating → overlay. Dark mode is a near-black dashboard ground with
 * charcoal layers, not Mantine's default navy-gray; pure black is reserved for deliberate full-bleed media.
 *
 * Horizon (picked on 2026-10-13 from the four /home2 design previews): on light the page is a cool
 * blue-grey and every card, the sidebar included, is white on it, lifted by a soft shadow rather than outlined; on dark
 * the page drops to near-black so the charcoal cards stand off it.
 */
export interface SurfaceTokens {
  /** Application background. Not a card. */
  readonly page: string;
  /** Secondary wells: chart and table recesses, control tracks. */
  readonly sunken: string;
  /** Standard cards, tables, filter regions, sidebars. */
  readonly panel: string;
  /** Controls inside panels: inputs, selects, segmented controls, compact toolbars. */
  readonly raised: string;
  /** Menus, popovers, dropdowns, tooltips. */
  readonly floating: string;
  /** Modal and drawer surfaces over the approved scrim. */
  readonly overlay: string;
  /**
   * A navigation row under the pointer, and the resting fill of the selected row. Its own token because the two
   * schemes need different steps: on dark it lands on `raised`, but on light `raised` is the same white as the page,
   * so a hover there has to come from the sunken side instead.
   */
  readonly navHover: string;
  /** The selected navigation row under the pointer: one step further than `navHover`. */
  readonly navActiveHover: string;
  readonly borderSubtle: string;
  readonly borderDefault: string;
  readonly borderStrong: string;
  readonly textPrimary: string;
  readonly textSecondary: string;
  readonly textMuted: string;
  readonly textDisabled: string;
}

export const LIGHT_SURFACES: SurfaceTokens = {
  page: "#EEF2F8",
  sunken: "#F2F5FA",
  panel: "#FFFFFF",
  raised: "#FFFFFF",
  floating: "#FFFFFF",
  overlay: "#FFFFFF",
  navHover: "#F2F4F7",
  navActiveHover: "#E9ECF1",
  borderSubtle: "#E6E8EC",
  borderDefault: "#D5D9DF",
  borderStrong: "#B4BAC4",
  textPrimary: "#0A0B0D",
  textSecondary: "#5B616E",
  textMuted: "#626875",
  textDisabled: "#A3A9B4",
};

export const DARK_SURFACES: SurfaceTokens = {
  page: "#07080B",
  sunken: "#0D0F13",
  panel: "#121419",
  raised: "#1C1E23",
  floating: "#1E2025",
  overlay: "#202328",
  /** On the sidebar card (the panel) since Horizon: 1.13:1 against it, where #1A1C21 was 1.08:1. */
  navHover: "#1E2025",
  navActiveHover: "#22252B",
  borderSubtle: "#24272D",
  borderDefault: "#32353D",
  borderStrong: "#464A53",
  textPrimary: "#F7F8FA",
  textSecondary: "#AEB4BF",
  textMuted: "#8A919E",
  textDisabled: "#5B616E",
};

/** Pure black is never a default surface; it is available only for deliberate immersive/full-bleed media. */
export const IMMERSIVE_BLACK = "#000000";

const VARIABLE_NAMES: Readonly<Record<keyof SurfaceTokens, string>> = {
  page: "--how-surface-page",
  sunken: "--how-surface-sunken",
  panel: "--how-surface-panel",
  raised: "--how-surface-raised",
  floating: "--how-surface-floating",
  overlay: "--how-surface-overlay",
  navHover: "--how-nav-hover",
  navActiveHover: "--how-nav-active-hover",
  borderSubtle: "--how-border-subtle",
  borderDefault: "--how-border-default",
  borderStrong: "--how-border-strong",
  textPrimary: "--how-text-primary",
  textSecondary: "--how-text-secondary",
  textMuted: "--how-text-muted",
  textDisabled: "--how-text-disabled",
};

function toVariables(tokens: SurfaceTokens): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, name] of Object.entries(VARIABLE_NAMES)) {
    out[name] = tokens[key as keyof SurfaceTokens];
  }
  return out;
}

/**
 * How a card sits on the page (Horizon). On light a white card on the blue-grey page has no outline; a soft shadow
 * carries the edge, so `--how-panel-edge` is transparent. On dark a shadow cannot be seen against near-black, so the
 * card keeps its quiet border and a hairline of light along the top.
 */
const LIGHT_CARD: Record<string, string> = {
  "--how-shadow-panel": "0 1px 2px rgb(16 24 40 / 0.04), 0 10px 28px -14px rgb(16 24 40 / 0.16)",
  "--how-shadow-panel-hover":
    "0 1px 2px rgb(16 24 40 / 0.05), 0 16px 32px -14px rgb(16 24 40 / 0.24)",
  "--how-panel-edge": "transparent",
};
const DARK_CARD: Record<string, string> = {
  "--how-shadow-panel": "inset 0 1px 0 rgb(255 255 255 / 0.04)",
  "--how-shadow-panel-hover": "inset 0 1px 0 rgb(255 255 255 / 0.06)",
  "--how-panel-edge": DARK_SURFACES.borderSubtle,
};

/** CSS variable name for a surface token, for the rare component that needs it in TypeScript. */
export function surfaceVar(token: keyof SurfaceTokens): string {
  return `var(${VARIABLE_NAMES[token]})`;
}

/**
 * Emits the surface tokens per color scheme. Mantine scopes the light/dark blocks itself, so a component can read
 * `var(--how-surface-panel)` without a scheme conditional. Passed to MantineProvider by `AppProviders`.
 */
export const cssVariablesResolver: CSSVariablesResolver = () => ({
  variables: {
    "--how-shadow-floating":
      "0 8px 24px -8px rgba(3, 5, 8, 0.28), 0 2px 6px -2px rgba(3, 5, 8, 0.2)",
    "--how-motion-fast": "140ms",
    "--how-motion-overlay": "190ms",
    "--how-motion-ease": "cubic-bezier(0.2, 0.8, 0.3, 1)",
  },
  light: { ...toVariables(LIGHT_SURFACES), ...LIGHT_CARD },
  dark: { ...toVariables(DARK_SURFACES), ...DARK_CARD },
});
